import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Bot, Plus, Settings, Trash2, Play, Square, Edit, MoreVertical, QrCode, X, StopCircle } from 'lucide-react'
import { botService } from '../services/botService'
import { Loading } from '../components/common/Loading'
import { useWebSocket } from '../hooks/useWebSocket'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

interface BotData {
  id: number
  session_name: string
  phone_number: string | null
  status: 'disconnected' | 'authenticating' | 'connected' | 'failed'
  last_connection: string | null
  created_at: string
  updated_at: string
  settings: any
}

const Bots: React.FC = () => {
  const navigate = useNavigate()
  const [bots, setBots] = useState<BotData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showActions, setShowActions] = useState<number | null>(null)
  const [showQRCode, setShowQRCode] = useState<number | null>(null)
  const [qrCodeData, setQrCodeData] = useState<string | null>(null)

  const { on, off } = useWebSocket()

  useEffect(() => {
    loadBots()
  }, [])

  useEffect(() => {
    // Configurar listeners WebSocket
    const handleBotStatusUpdate = (data: { botId: number; status: string; qrCode?: string; timestamp: string }) => {
      logger.debug('🔔 WebSocket: bot-status-update recebido:', data)
      setBots(prev => prev.map(bot =>
        bot.id === data.botId
          ? {
              ...bot,
              status: data.status as any,
              ...(data.status === 'authenticating'
                ? { qr_code: data.qrCode }
                : { qr_code: undefined })
            }
          : bot
      ))
      // Só exibir QR no modal durante o fluxo de pareamento (evita reabrir após connected por evento "qr" tardio)
      if (data.status === 'authenticating' && data.qrCode) {
        setQrCodeData(data.qrCode)
        setShowQRCode(data.botId)
      }
    }

    const handleBotConnectionUpdate = (data: { botId: number; isConnected: boolean; timestamp: string }) => {
      logger.debug('🔔 WebSocket: bot-connection-update recebido:', data)
      setBots(prev => prev.map(bot => 
        bot.id === data.botId 
          ? { ...bot, status: data.isConnected ? 'connected' : 'disconnected' }
          : bot
      ))
      if (data.isConnected && showQRCode === data.botId) {
        setShowQRCode(null)
        setQrCodeData(null)
      }
    }

    const handleQRCodeScanned = (data: { botId: number; status: string; timestamp: string }) => {
      logger.debug('🔔 WebSocket: qr-code-scanned recebido:', data)
      
      // Atualizar status do bot
      setBots(prev => prev.map(bot => 
        bot.id === data.botId 
          ? { ...bot, status: 'connected' }
          : bot
      ))
      
      // Fechar modal de QR Code
      if (showQRCode === data.botId) {
        setShowQRCode(null)
        setQrCodeData(null)
      }
      
      // Mostrar alert de sucesso
      toast.success('✅ Bot conectado com sucesso!', {
        duration: 4000,
        position: 'top-center'
      })
      
      // Recarregar lista de bots após 1 segundo
      setTimeout(() => {
        loadBots()
      }, 1000)
    }

    const handleBotDisconnected = (data: { botId: number; status: string; timestamp: string }) => {
      logger.debug('🔔 WebSocket: bot-disconnected recebido:', data)
      
      // Atualizar status do bot
      setBots(prev => prev.map(bot => 
        bot.id === data.botId 
          ? { ...bot, status: 'disconnected' }
          : bot
      ))
      
      // Mostrar alert de desconexão
      toast.error('❌ Bot desconectado', {
        duration: 4000,
        position: 'top-center'
      })
    }

    on('bot-status-update', handleBotStatusUpdate)
    on('bot-connection-update', handleBotConnectionUpdate)
    on('qr-code-scanned', handleQRCodeScanned)
    on('bot-disconnected', handleBotDisconnected)

    return () => {
      off('bot-status-update', handleBotStatusUpdate)
      off('bot-connection-update', handleBotConnectionUpdate)
      off('qr-code-scanned', handleQRCodeScanned)
      off('bot-disconnected', handleBotDisconnected)
    }
  }, [on, off, showQRCode])

  const loadBots = async () => {
    try {
      setIsLoading(true)
      const result = await botService.getBots()
      
      if (result.success && result.data?.bots) {
        setBots(result.data.bots || [])
      } else {
        toast.error('Erro ao carregar bots')
      }
    } catch (error) {
      logger.error('Erro ao carregar bots:', error)
      toast.error('Erro ao carregar bots')
    } finally {
      setIsLoading(false)
    }
  }


  const handleConnect = async (botId: number) => {
    try {
      // Atualizar status local imediatamente
      setBots(prevBots => 
        prevBots.map(bot => 
          bot.id === botId 
            ? { ...bot, status: 'authenticating' }
            : bot
        )
      )
      
      const result = await botService.connectBot(botId)
      logger.debug('Resultado do connectBot:', result)
      
      if (result.success) {
        if (result.qrCode) {
          toast.success('Iniciando conexão...')
          setQrCodeData(result.qrCode)
          setShowQRCode(botId)
        } else if (result.isConnected) {
          toast.success('Bot já está conectado!')
          setBots(prevBots => 
            prevBots.map(bot => 
              bot.id === botId 
                ? { ...bot, status: 'connected' }
                : bot
            )
          )
        }
      } else {
        // Manter status 'authenticating'; abrir modal (não limpar qrCodeData - o WebSocket pode já ter enviado o QR)
        toast.success('Aguardando QR Code...')
        setShowQRCode(botId)
      }
    } catch (error) {
      logger.error('Erro ao conectar bot:', error)
      // Reverter status em caso de erro
      setBots(prevBots => 
        prevBots.map(bot => 
          bot.id === botId 
            ? { ...bot, status: 'disconnected' }
            : bot
        )
      )
      toast.error('Erro ao conectar bot')
    }
  }

  const handleShowQRCode = async (botId: number) => {
    try {
      const result = await botService.getBotStatus(botId)
      logger.debug('Resultado do getBotStatus:', result)
      
      // Verificar diferentes estruturas de resposta
      const qrCode = result.qrCode
      
      if (qrCode) {
        setQrCodeData(qrCode)
        setShowQRCode(botId)
      } else {
        logger.debug('QR Code não encontrado na resposta:', result)
        toast.error('QR Code não disponível')
      }
    } catch (error) {
      logger.error('Erro ao obter QR Code:', error)
      toast.error('Erro ao obter QR Code')
    }
  }

  const handleDisconnect = async (botId: number) => {
    try {
      // Atualizar status local imediatamente
      setBots(prevBots => 
        prevBots.map(bot => 
          bot.id === botId 
            ? { ...bot, status: 'disconnected' }
            : bot
        )
      )
      
      const result = await botService.disconnectBot(botId)
      logger.debug('Resultado do disconnectBot:', result)
      
      if (result.success || result.data?.success) {
        toast.success('Bot desconectado')
        // Fechar modal de QR code se estiver aberto
        if (showQRCode === botId) {
          setShowQRCode(null)
          setQrCodeData('')
        }
      } else {
        // Reverter status em caso de erro
        setBots(prevBots => 
          prevBots.map(bot => 
            bot.id === botId 
              ? { ...bot, status: 'connected' }
              : bot
          )
        )
        toast.error(result.message || result.data?.message || 'Erro ao desconectar bot')
      }
    } catch (error) {
      logger.error('Erro ao desconectar bot:', error)
      // Reverter status em caso de erro
      setBots(prevBots => 
        prevBots.map(bot => 
          bot.id === botId 
            ? { ...bot, status: 'connected' }
            : bot
        )
      )
      toast.error('Erro ao desconectar bot')
    }
  }

  const handleCancelAuthentication = async (botId: number) => {
    try {
      // Atualizar status localmente imediatamente
      setBots(prevBots => 
        prevBots.map(bot => 
          bot.id === botId 
            ? { ...bot, status: 'disconnected' }
            : bot
        )
      )

      const result = await botService.cancelAuthentication(botId)
      logger.debug('Resultado do cancelAuthentication:', result)
      
      if (result.success || result.data?.success) {
        toast.success('Autenticação cancelada')
        // Fechar modal de QR code se estiver aberto
        if (showQRCode === botId) {
          setShowQRCode(null)
          setQrCodeData('')
        }
      } else {
        // Reverter status em caso de erro
        setBots(prevBots => 
          prevBots.map(bot => 
            bot.id === botId 
              ? { ...bot, status: 'authenticating' }
              : bot
          )
        )
        toast.error(result.message || result.data?.message || 'Erro ao cancelar autenticação')
      }
    } catch (error) {
      logger.error('Erro ao cancelar autenticação:', error)
      // Reverter status em caso de erro
      setBots(prevBots => 
        prevBots.map(bot => 
          bot.id === botId 
            ? { ...bot, status: 'authenticating' }
            : bot
        )
      )
      toast.error('Erro ao cancelar autenticação')
    }
  }

  const handleEdit = (botId: number) => {
    navigate(`/bots/${botId}/edit`)
  }

  const handleDelete = async (botId: number) => {
    if (!confirm('Tem certeza que deseja deletar este bot? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      const result = await botService.deleteBot(botId)
      
      if (result.success) {
        toast.success('Bot deletado com sucesso')
        loadBots()
      } else {
        toast.error(result.message || 'Erro ao deletar bot')
      }
    } catch (error) {
      logger.error('Erro ao deletar bot:', error)
      toast.error('Erro ao deletar bot')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800'
      case 'authenticating':
        return 'bg-yellow-100 text-yellow-800'
      case 'disconnected':
        return 'bg-red-100 text-red-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return 'Conectado'
      case 'authenticating':
        return 'Autenticando'
      case 'disconnected':
        return 'Desconectado'
      case 'failed':
        return 'Falhou'
      default:
        return 'Desconhecido'
    }
  }


  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nunca'
    return new Date(dateString).toLocaleString('pt-BR')
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center min-h-96">
          <Loading />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Bots
              </h1>
              <p className="text-gray-600">Gerencie seus chatbots WhatsApp</p>
            </div>
          </div>
            <button 
              onClick={() => navigate('/bots/create')}
              className="btn btn-primary btn-md">
              <Plus className="w-5 h-5 mr-2" />
              Novo Bot
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Bot className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Bots</p>
              <p className="text-2xl font-bold text-gray-900">{bots.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Play className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Conectados</p>
              <p className="text-2xl font-bold text-gray-900">
                {bots.filter(bot => bot.status === 'connected').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <Square className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Desconectados</p>
              <p className="text-2xl font-bold text-gray-900">
                {bots.filter(bot => bot.status === 'disconnected').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bots List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Lista de Bots</h2>
        </div>
        
        {bots.length === 0 ? (
          <div className="p-12 text-center">
            <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum bot criado</h3>
            <p className="text-gray-600 mb-4">Crie seu primeiro bot para começar</p>
            <button
              onClick={() => navigate('/bots/create')}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Bot
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {bots.map((bot) => (
              <div key={bot.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Bot className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {bot.session_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {bot.phone_number || 'Número não configurado'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Criado em: {formatDate(bot.created_at)}
                      </p>
                      {bot.last_connection && (
                        <p className="text-xs text-gray-500">
                          Última conexão: {formatDate(bot.last_connection)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(bot.status)}`}>
                      {getStatusText(bot.status)}
                    </span>
                    
                    <div className="flex space-x-2">
                      {/* Botão de ação principal */}
                      {bot.status === 'disconnected' && (
                        <button 
                          onClick={() => handleConnect(bot.id)}
                          className="btn btn-success btn-sm"
                          title="Conectar"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      
                      {bot.status === 'connected' && (
                        <button 
                          onClick={() => handleDisconnect(bot.id)}
                          className="btn btn-warning btn-sm"
                          title="Desconectar"
                        >
                          <Square className="w-4 h-4" />
                        </button>
                      )}

                      {bot.status === 'authenticating' && (
                        <>
                          <button 
                            onClick={() => handleShowQRCode(bot.id)}
                            className="btn btn-info btn-sm"
                            title="Ver QR Code"
                          >
                            <QrCode className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleCancelAuthentication(bot.id)}
                            className="btn btn-danger btn-sm"
                            title="Cancelar Autenticação"
                          >
                            <StopCircle className="w-4 h-4" />
                          </button>
                          <button 
                            disabled
                            className="btn btn-secondary btn-sm opacity-50"
                            title="Autenticando..."
                          >
                            <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                          </button>
                        </>
                      )}

                      {bot.status === 'failed' && (
                        <button 
                          onClick={() => handleConnect(bot.id)}
                          className="btn btn-success btn-sm"
                          title="Tentar Conectar Novamente"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}

                      {/* Menu de ações */}
                      <div className="relative">
                        <button
                          onClick={() => setShowActions(showActions === bot.id ? null : bot.id)}
                          className="btn btn-outline btn-sm"
                          title="Mais ações"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {showActions === bot.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handleEdit(bot.id)
                                  setShowActions(null)
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Edit className="w-4 h-4 mr-3" />
                                Editar Bot
                              </button>
                              
                              <button
                                onClick={() => {
                                  navigate(`/bots/${bot.id}`)
                                  setShowActions(null)
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Settings className="w-4 h-4 mr-3" />
                                Configurações
                              </button>
                              
                              <hr className="my-1" />
                              
                              <button
                                onClick={() => {
                                  handleDelete(bot.id)
                                  setShowActions(null)
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-3" />
                                Deletar Bot
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Click outside to close actions menu */}
      {showActions && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowActions(null)}
        />
      )}

      {/* QR Code Modal - abre ao conectar ou quando WebSocket envia o QR */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">QR Code para Conectar</h3>
              <button
                onClick={() => {
                  setShowQRCode(null)
                  setQrCodeData(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                {qrCodeData
                  ? 'Escaneie este QR Code com seu WhatsApp para conectar o bot'
                  : 'Aguardando QR Code...'}
              </p>
              
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block min-w-[272px] min-h-[272px] flex items-center justify-center">
                {qrCodeData ? (
                  <img 
                    src={qrCodeData}
                    alt="QR Code para conectar WhatsApp"
                    className="w-64 h-64 mx-auto"
                  />
                ) : (
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                {qrCodeData
                  ? 'Abra o WhatsApp no seu celular e escaneie este código'
                  : 'O QR Code será exibido em instantes'}
              </p>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowQRCode(null)
                  setQrCodeData(null)
                }}
                className="btn btn-md btn-outline"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default Bots